// wcwb/frontend/src/components/CommentsThread.js
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const AVATAR_COLORS = ['#C4612A','#5D4037','#1A237E','#1B5E20','#BF360C','#4A148C'];

function timeAgo(date) {
  const mins = Math.floor((Date.now() - new Date(date)) / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return new Date(date).toLocaleDateString('en-US',{month:'short',day:'numeric'});
}

export default function CommentsThread({ event }) {
  const { token, profile } = useAuth();
  const [comments, setComments] = useState([]);
  const [message, setMessage]   = useState('');
  const [posting, setPosting]   = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { fetchComments(); }, [event.id]);

  // Poll every 15 seconds for new comments
  useEffect(() => {
    const interval = setInterval(fetchComments, 15000);
    return () => clearInterval(interval);
  }, [event.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  async function fetchComments() {
    try {
      const res = await fetch(`${API}/comments?event_id=${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) setComments(data);
    } catch(e) { console.error(e); }
  }

  async function postComment(e) {
    e.preventDefault();
    if (!message.trim() || posting) return;
    setPosting(true);
    try {
      const res = await fetch(`${API}/comments`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ event_id: event.id, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setComments(prev => [...prev, data]);
      setMessage('');
    } catch(e) { console.error(e); }
    finally { setPosting(false); }
  }

  async function deleteComment(id) {
    try {
      await fetch(`${API}/comments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(prev => prev.filter(c => c.id !== id));
    } catch(e) { console.error(e); }
  }

  return (
    <div>
      <p style={{ fontSize:13, color:'#7A6555', marginBottom:16 }}>
        Chat with your fellow guests — coordinate, get excited, share tips!
      </p>

      {/* Comment list */}
      <div style={{ maxHeight:420, overflowY:'auto', marginBottom:16, display:'flex', flexDirection:'column', gap:10 }}>
        {comments.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px 20px' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>💬</div>
            <p style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, marginBottom:6 }}>No messages yet</p>
            <p style={{ color:'#7A6555', fontSize:13 }}>Be the first to say something!</p>
          </div>
        )}
        {comments.map(c => {
          const isMe = c.user_id === profile?.id;
          const initials = c.profiles?.full_name?.split(' ').map(w=>w[0]).join('').toUpperCase() || '?';
          const bg = AVATAR_COLORS[(c.user_id||'').charCodeAt(0) % AVATAR_COLORS.length];
          return (
            <div key={c.id} style={{ display:'flex', gap:10, alignItems:'flex-start', flexDirection: isMe ? 'row-reverse' : 'row' }}>
              <div style={{ width:32, height:32, borderRadius:'50%', background:bg, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontSize:12, fontWeight:500, flexShrink:0 }}>
                {initials}
              </div>
              <div style={{ maxWidth:'75%' }}>
                <div style={{ display:'flex', gap:6, alignItems:'baseline', marginBottom:3, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                  <span style={{ fontSize:12, fontWeight:500, color:'#2C1A0E' }}>{isMe ? 'You' : c.profiles?.full_name}</span>
                  <span style={{ fontSize:11, color:'#7A6555' }}>{timeAgo(c.created_at)}</span>
                </div>
                <div style={{
                  background: isMe ? '#C4612A' : 'white',
                  color: isMe ? 'white' : '#2C1A0E',
                  border: isMe ? 'none' : '1px solid #E5D9C8',
                  borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  padding: '10px 14px',
                  fontSize: 14,
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                }}>
                  {c.message}
                </div>
                {isMe && (
                  <button onClick={() => deleteComment(c.id)}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'#B71C1C', fontSize:11, marginTop:4, display:'block', textAlign:'right' }}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <form onSubmit={postComment} style={{ display:'flex', gap:8 }}>
        <input
          style={{ flex:1, padding:'10px 13px', border:'1px solid #E5D9C8', borderRadius:10, fontFamily:'Jost,sans-serif', fontSize:14, color:'#2C1A0E', outline:'none' }}
          placeholder="Say something to the group…"
          value={message}
          onChange={e => setMessage(e.target.value)}
        />
        <button type="submit"
          style={{ background:'#C4612A', color:'white', border:'none', borderRadius:10, padding:'10px 18px', fontFamily:'Jost,sans-serif', fontSize:14, fontWeight:500, cursor:'pointer', opacity:(!message.trim()||posting)?0.5:1 }}
          disabled={!message.trim() || posting}>
          {posting ? '…' : 'Send'}
        </button>
      </form>
    </div>
  );
}
